import {
  BikeStation,
  BikeStationService,
  ComputedRoute,
  ComputedRouteService,
  GeocoderService,
  Place,
  Search,
  useCancellablePromise,
  useTracker,
} from '@geovelo-frontends/commons';
import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ComputedRouteDetailsQuery } from '../../../graphql-types';
import RoutingDetails from '../../components/routing/details';
import ReportDialog from '../../components/routing/report-dialog';
import Seo from '../../components/seo';
import { TPageProps } from '../../page-props';

function ComputedRouteDetailsPage(props: TPageProps<ComputedRouteDetailsQuery>) {
  const [computedRoute, setComputedRoute] = useState<ComputedRoute | null>();
  const [bikeStationsMap, setBikeStationsMap] = useState<{ [key: number]: BikeStation }>({});
  const [downloading, setDownloading] = useState(false);
  const [reportDialogOpen, openReportDialog] = useState(false);
  const { t } = useTranslation();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { trackEvent } = useTracker();

  useEffect(() => {
    const { computedRouteId } = props.params;
    if (
      computedRouteId !== ':computedRouteId' &&
      computedRouteId !== '[computedRouteId]' &&
      computedRouteId !== 'search' &&
      computedRouteId !== '/search'
    )
      getComputedRoute(computedRouteId);
    else setComputedRoute(null);

    return () => {
      cancelPromises();
    };
  }, []);

  async function getComputedRoute(id: string) {
    cancelPromises();

    try {
      const _computedRoute = await cancellablePromise(
        ComputedRouteService.get(id, {
          instructions: true,
          roadCyclingQualities: true,
          showPushingBikeInstructions: true,
          geometry: true,
          elevations: true,
          facilities: true,
          energy: true,
        }),
      );
      const { sections, bikeStationIds } = _computedRoute;
      const { wayPoints: lastSectionWayPoints } = sections[sections.length - 1];
      const arrival = lastSectionWayPoints[lastSectionWayPoints.length - 1];
      const points: GeoJSON.Point[] = [];
      sections.forEach(({ wayPoints }) => {
        wayPoints.splice(0, wayPoints.length - 1).forEach(({ point }) => points.push(point));
      });

      const wayPoints = await cancellablePromise(
        Promise.all<Place>([
          ...points.map<Promise<Place>>((point) => {
            return new Promise((resolve) => {
              GeocoderService.reverseGeocode(undefined, point, (place) => resolve(place));
            });
          }),
          new Promise((resolve) => {
            GeocoderService.reverseGeocode(undefined, arrival.point, (place) => resolve(place));
          }),
        ]),
      );

      _computedRoute.wayPoints = [...wayPoints];

      if (bikeStationIds.length > 0) {
        const bikeStations = await cancellablePromise(
          BikeStationService.getBikeStations({ ids: bikeStationIds }),
        );

        setBikeStationsMap(
          bikeStations.reduce<{ [key: number]: BikeStation }>((res, bikeStation) => {
            res[bikeStation.id] = bikeStation;

            return res;
          }, {}),
        );
      }

      if (window.locations?.length === 1) {
        const {
          cyclingProfile,
          bikeStationIds,
          eBike: eBikeEnabled,
          transportModes,
          wayPoints,
        } = _computedRoute;

        const search = new Search({
          profile: cyclingProfile,
          bikeType:
            cyclingProfile === 'cargo'
              ? 'cargo'
              : cyclingProfile === 'hybridBike'
                ? 'hybridBike'
                : bikeStationIds.length > 0
                  ? 'shared'
                  : 'own',
          eBikeEnabled,
          transportModes,
          wayPoints,
        });

        window.locations[0].state = { search };
      }

      setComputedRoute(_computedRoute);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setComputedRoute(null);
      }
    }
  }

  async function handleDownload() {
    if (!computedRoute) return;

    const { wayPoints } = computedRoute;

    setDownloading(true);
    trackEvent('Computed route GPX', 'Downloaded');

    try {
      const blob = await ComputedRouteService.getGPX(computedRoute.id);

      saveAs(
        blob,
        `${wayPoints[0].address.replace(/ /g, '_')}-${wayPoints[
          wayPoints.length - 1
        ].address.replace(/ /g, '_')}.gpx`,
      );
    } catch (err) {
      console.error(err);
    }

    setDownloading(false);
  }

  return (
    <>
      <Seo
        description={
          props.data.prismicHome?.data.description?.text ||
          t('geovelo.pages_descriptions.home') ||
          null
        }
        title={`${t('geovelo.pages_titles.routing')}`}
        {...props}
      />
      <RoutingDetails
        bikeStationsMap={bikeStationsMap}
        downloading={downloading}
        onDownload={handleDownload}
        onReport={() => openReportDialog(true)}
        path={props.path}
        route={computedRoute}
      />
      {computedRoute && (
        <ReportDialog
          computedRoute={computedRoute}
          onClose={() => openReportDialog(false)}
          open={reportDialogOpen}
        />
      )}
    </>
  );
}

export default ComputedRouteDetailsPage;
